import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import { useFasesDosVendedores, FaseBadge } from '../components/FaseBadge';
import './MetasConfig.css';

const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

const emptyForm = {
    id: null,
    store_id: '',
    salesman_id: '',
    date_start: '',
    date_end: '',
    valid_attendance: '',
    questions: '',
    recording_hours: '',
    invalid_attendance: '',
    daily_goal: '',
    salesman_count: '',
};

/**
 * `scope` é OPCIONAL e vem do ScopeProvider quando esta tela roda como aba de Configurações
 * (§9.3). Sem ele — na rota antiga /metas-config — a tela mantém os próprios selects e se comporta
 * exatamente como antes. É o que permite a transição sem janela de regressão: as duas formas
 * coexistem até o item antigo do menu ser escondido.
 */
function MetasConfig({ user, scope }) {
    const escopoExterno = !!scope?.subdomain;
    // Modo FASE: a meta é da fase, não de um período. A janela vem da trilha do MEMBRO
    // (phase_transitions) e é materializada na resposta do /goals — por isso o formulário não tem
    // data nem loja aqui. Guardar data na meta duplicaria a janela e deixaria as duas divergirem.
    const modoFase = escopoExterno && scope.kind === 'phase';
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain: escopoExterno ? scope.subdomain : undefined });
    const userId = user?.user_id;
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [subdomainLocal, setSubdomain] = useState('');
    const [filterStoreIdLocal, setFilterStoreId] = useState('');
    // Com escopo externo, ele MANDA: a tela não pode divergir do que o seletor mostra.
    const subdomain = escopoExterno ? scope.subdomain : subdomainLocal;
    const filterStoreId = escopoExterno
        ? (scope.kind === 'store' ? String(scope.store_id) : '')
        : filterStoreIdLocal;

    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [formSalesmen, setFormSalesmen] = useState([]);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);  // guard síncrono contra double-submit (o disabled={saving} é async e não pega 2 cliques rápidos)

    // Empresas (admin)
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify]);

    // Lojas ao trocar empresa
    useEffect(() => {
        setStores([]); setFilterStoreId(''); setGoals([]); setFormOpen(false);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, notify]);

    const loadGoals = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            if (modoFase) {
                const r = await apiService.phaseGoalsList({ subdomain, phase_id: scope.phase_id });
                setGoals((r?.data?.goals || []).map((g) => ({ ...g, _fase: true })));
            } else {
                const r = await apiService.pointsGoalsList({ subdomain, store_id: filterStoreId || undefined });
                setGoals(r.goals || []);
            }
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar metas', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, filterStoreId, notify, modoFase, scope?.phase_id]);

    // Metas ao trocar empresa/filtro de loja
    useEffect(() => { loadGoals(); }, [loadGoals]);

    // Vendedores da loja escolhida NO FORMULÁRIO
    useEffect(() => {
        setFormSalesmen([]);
        if (!subdomain || !form.store_id) return;
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: form.store_id });
                setFormSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain, form.store_id, userId]);

    const openNewForm = () => {
        setForm({ ...emptyForm, store_id: filterStoreId || '' });
        setFormOpen(true);
    };

    const openEditForm = (g) => {
        setForm({
            id: g.id,
            // Meta de fase não tem loja nem datas: os campos ficam vazios e o formulário os esconde.
            store_id: g.store_id != null ? String(g.store_id) : '',
            salesman_id: g.salesman_id ? String(g.salesman_id) : '',
            date_start: g.date_start || '',
            date_end: g.date_end || '',
            valid_attendance: g.valid_attendance ?? '',
            questions: g.questions ?? '',
            recording_hours: g.recording_hours ?? '',
            invalid_attendance: g.invalid_attendance ?? '',
            daily_goal: g.daily_goal ?? '',
            salesman_count: g.salesman_count ?? '',
        });
        setFormOpen(true);
    };

    const closeForm = () => { setFormOpen(false); setForm(emptyForm); };

    const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const alvosPreenchidos = form.valid_attendance !== '' && form.questions !== '' &&
        form.recording_hours !== '' && form.invalid_attendance !== '' && form.daily_goal !== '';
    const canSave = modoFase
        ? alvosPreenchidos
        : (form.store_id && form.date_start && form.date_end && alvosPreenchidos);

    const save = async () => {
        if (!canSave) { notify('Preencha todos os campos obrigatórios', 'error'); return; }
        if (savingRef.current) return;   // evita 2ª requisição concorrente (double-submit -> 409 falso)
        savingRef.current = true;
        setSaving(true);
        try {
            if (modoFase) {
                await apiService.phaseGoalSave({
                    subdomain,
                    id: form.id || undefined,
                    phase_id: scope.phase_id,
                    // salesman_id vazio = meta de toda a fase; preenchido = override individual.
                    // A cascata da API é vendedor > turma > fase.
                    salesman_id: form.salesman_id ? Number(form.salesman_id) : null,
                    valid_attendance: Number(form.valid_attendance),
                    questions: Number(form.questions),
                    recording_hours: Number(form.recording_hours),
                    invalid_attendance: Number(form.invalid_attendance),
                    daily_goal: Number(form.daily_goal),
                });
                notify(form.id ? 'Meta da fase atualizada' : 'Meta da fase criada', 'success');
                closeForm();
                loadGoals();
                return;
            }
            await apiService.pointsGoalsSave({
                id: form.id || undefined,
                subdomain,
                store_id: Number(form.store_id),
                salesman_id: form.salesman_id ? Number(form.salesman_id) : null,
                date_start: form.date_start,
                date_end: form.date_end,
                valid_attendance: Number(form.valid_attendance),
                questions: Number(form.questions),
                recording_hours: Number(form.recording_hours),
                invalid_attendance: Number(form.invalid_attendance),
                daily_goal: Number(form.daily_goal),
                salesman_count: form.salesman_count !== '' ? Number(form.salesman_count) : null,
            });
            notify(form.id ? 'Meta atualizada' : 'Meta criada', 'success');
            closeForm();
            loadGoals();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao salvar meta', 'error');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const remove = async (g) => {
        const label = g.salesman_id ? `vendedor ${g.salesman_name || g.salesman_id}`
            : g._fase ? `fase ${g.phase_name}` : `loja ${g.store_name}`;
        const periodo = g._fase ? '' : ` (${brDate(g.date_start)}–${brDate(g.date_end)})`;
        if (!(await confirmar(`Excluir a meta de ${label}${periodo}?`, { titulo: 'Excluir meta', perigo: true, rotuloConfirmar: 'Excluir' }))) return;
        try {
            if (g._fase) await apiService.phaseGoalDelete({ subdomain, id: g.id });
            else await apiService.pointsGoalsDelete({ subdomain, id: g.id });
            notify('Meta excluída', 'success');
            loadGoals();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao excluir meta', 'error');
        }
    };

    return (
        <div className="mc-page">
            {toast && <div className={`mc-toast mc-toast-${toast.type}`}>{toast.msg}</div>}

            {!escopoExterno && <h1 className="mc-title">🎯 Configuração de Metas</h1>}

            <div className="mc-filters">
                {!escopoExterno && (
                    <>
                        <div className="mc-field">
                            <label>Empresa</label>
                            <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                                <option value="">— Selecione —</option>
                                {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="mc-field">
                            <label>Loja</label>
                            <select value={filterStoreId} onChange={(e) => setFilterStoreId(e.target.value)} disabled={!subdomain}>
                                <option value="">Todas</option>
                                {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                            </select>
                        </div>
                    </>
                )}
                <div className="mc-field mc-filters-actions">
                    <button className="mc-btn mc-btn-primary" onClick={openNewForm} disabled={!subdomain}>
                        ➕ Nova meta
                    </button>
                </div>
            </div>

            {formOpen && (
                <div className="mc-form-panel">
                    <h2>{form.id ? 'Editar meta' : 'Nova meta'}</h2>
                    <div className="mc-form-grid">
                        {!modoFase && (
                        <div className="mc-field">
                            <label>Loja *</label>
                            <select value={form.store_id} onChange={(e) => setForm((prev) => ({ ...prev, store_id: e.target.value, salesman_id: '' }))}>
                                <option value="">— Selecione —</option>
                                {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                            </select>
                        </div>
                        )}
                        <div className="mc-field">
                            <label>Vendedor (opcional)</label>
                            <select value={form.salesman_id} onChange={(e) => setField('salesman_id', e.target.value)} disabled={!modoFase && !form.store_id}>
                                <option value="">{modoFase ? '— Meta de toda a fase —' : '— Meta da loja (padrão) —'}</option>
                                {formSalesmen.map((s) => (
                                    <option key={s.salesman_id} value={s.salesman_id}>
                                        {s.salesman_name}{s.is_active ? '' : ' (inativo)'}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {modoFase ? (
                            <div className="mc-field mc-field-wide">
                                <label>Período</label>
                                <p className="mc-nota">
                                    Meta de fase não tem data: ela vale pela <strong>travessia</strong> da fase, e a
                                    janela de cada vendedor sai da trilha dele — quem entrou depois tem a própria.
                                </p>
                            </div>
                        ) : (
                        <>
                        <div className="mc-field">
                            <label>Data início *</label>
                            <input type="date" value={form.date_start} onChange={(e) => setField('date_start', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Data fim *</label>
                            <input type="date" value={form.date_end} onChange={(e) => setField('date_end', e.target.value)} />
                        </div>
                        </>
                        )}
                        <div className="mc-field">
                            <label>Atend. válidos *</label>
                            <input type="number" step="1" value={form.valid_attendance} onChange={(e) => setField('valid_attendance', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Perguntas *</label>
                            <input type="number" step="1" value={form.questions} onChange={(e) => setField('questions', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Horas de gravação *</label>
                            <input type="number" step="1" value={form.recording_hours} onChange={(e) => setField('recording_hours', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Atend. inválidos *</label>
                            <input type="number" step="1" value={form.invalid_attendance} onChange={(e) => setField('invalid_attendance', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Meta diária *</label>
                            <input type="number" step="1" value={form.daily_goal} onChange={(e) => setField('daily_goal', e.target.value)} />
                        </div>
                        {!modoFase && (
                        <div className="mc-field">
                            <label>Qtd. vendedores (override)</label>
                            <input type="number" step="1" value={form.salesman_count} onChange={(e) => setField('salesman_count', e.target.value)} placeholder="usa o real se vazio" />
                        </div>
                        )}
                    </div>
                    <div className="mc-form-actions">
                        <button className="mc-btn" onClick={closeForm} disabled={saving}>Cancelar</button>
                        <button className="mc-btn mc-btn-primary" onClick={save} disabled={saving || !canSave}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="mc-list">
                <h2>Metas cadastradas {loading ? '(carregando…)' : `(${goals.length})`}</h2>
                {!subdomain && <p className="mc-empty">Selecione uma empresa para ver as metas.</p>}
                {subdomain && !loading && goals.length === 0 && <p className="mc-empty">Nenhuma meta cadastrada.</p>}
                {goals.length > 0 && (
                    <table className="mc-table">
                        <thead>
                            <tr>
                                {modoFase ? <><th>Fase</th><th>Escopo</th></>
                                          : <><th>Loja</th><th>Vendedor</th><th>Início</th><th>Fim</th></>}
                                <th>Válidos</th><th>Perguntas</th><th>Horas</th><th>Inválidos</th>
                                <th>Meta diária</th>{!modoFase && <th>Qtd. vend.</th>}<th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {goals.map((g) => (
                                <tr key={g.id}>
                                    {modoFase ? (
                                        <>
                                            <td>{g.phase_name}</td>
                                            <td>{g.salesman_id ? `Vendedor ${g.salesman_id}`
                                                : g.cohort_id ? `Turma ${g.cohort_name || g.cohort_id}`
                                                : <em>Toda a fase</em>}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{g.store_name}</td>
                                            <td>{g.salesman_name || <em>Loja (padrão)</em>}{g.salesman_id && <FaseBadge fase={fasesVend[String(g.salesman_id)]} compacto />}</td>
                                            <td>{brDate(g.date_start)}</td>
                                            <td>{brDate(g.date_end)}</td>
                                        </>
                                    )}
                                    <td>{g.valid_attendance}</td>
                                    <td>{g.questions}</td>
                                    <td>{g.recording_hours}</td>
                                    <td>{g.invalid_attendance}</td>
                                    <td>{g.daily_goal}</td>
                                    {!modoFase && <td>{g.salesman_count ?? '—'}</td>}
                                    <td className="mc-actions">
                                        <button className="mc-btn mc-btn-sm" onClick={() => openEditForm(g)}>✏️ Editar</button>
                                        <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => remove(g)}>🗑️ Excluir</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default MetasConfig;
